import { LANGUAGES, type UiLanguage } from './types'

/** Typed access to the Vite environment, with development-safe defaults. */

export type AuthMode = 'entra' | 'mock'
export type DataSource = 'api' | 'fixtures'

/**
 * The same question as `env.dataSource === 'fixtures'`, asked in a way the
 * bundler can answer at build time.
 *
 * Vite substitutes `import.meta.env.VITE_*` with a literal, so this folds to
 * `false` in a production build and every `await import('./devData')` behind it
 * is dropped. Reading it through `env` instead would leave the bundler unable
 * to prove the branch is dead, and the 124 KB of development fixtures would
 * ship to the CDN as a chunk nothing ever loads.
 */
export const USE_FIXTURES: boolean = import.meta.env.VITE_DATA_SOURCE === 'fixtures'

function str(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.length > 0 ? value : fallback
}

export const env = {
  apiBaseUrl: str(import.meta.env.VITE_API_BASE_URL, '/api/v1').replace(/\/$/, ''),
  authMode: (str(import.meta.env.VITE_AUTH_MODE, 'entra') === 'mock' ? 'mock' : 'entra') as AuthMode,
  dataSource: (str(import.meta.env.VITE_DATA_SOURCE, 'api') === 'fixtures'
    ? 'fixtures'
    : 'api') as DataSource,
  entra: {
    tenantId: str(import.meta.env.VITE_ENTRA_TENANT_ID, ''),
    clientId: str(import.meta.env.VITE_ENTRA_SPA_CLIENT_ID, ''),
    apiScope: str(import.meta.env.VITE_ENTRA_API_SCOPE, ''),
  },
  allowedEmailDomains: str(import.meta.env.VITE_ALLOWED_EMAIL_DOMAINS, 'itpu.uz,student.itpu.uz')
    .split(',')
    .map((d) => d.trim().toLowerCase())
    .filter(Boolean),
  defaultLanguage: 'en' as UiLanguage,
  supportedLanguages: LANGUAGES,
  contactEmail: 'library@itpu.uz',
} as const

/**
 * Defence in depth only — the single-tenant Entra registration is the real gate,
 * and the API re-checks the domain server-side (§4.1).
 */
export function isAllowedEmail(email: string | undefined | null): boolean {
  if (!email) return false
  const domain = email.toLowerCase().split('@')[1]
  return !!domain && env.allowedEmailDomains.includes(domain)
}
